import { refuseUnported } from '../janus-engine/unported';
// inventory-breakdown.ts — inventory weight + GP breakdown charts (drill-down pies, top-lists, nav).
// Byte-faithful port of the ftInvWeight* / ftInvGp* cluster + the open*Breakdown entry points carved
// out of template.js (T1.1 inventory-breakdown carve, 2026-06-13). The Chart.js
// destroy-before-recreate lifecycle (ftInvWeightDestroyChart / ftInvGpDestroyChart called before each
// `new Chart`) is preserved verbatim to avoid canvas leaks.
//
// Public (window-attached): the open*Breakdown entry points (PHP onclick across _inventory_inc.php /
// _inventory_creature_unequipped_column_inc.php / get_container_weight_and_header_inc.php /
// table_item_object_inc.php + player-character-sheet bundle onclick strings), ftInvWeightGoUp /
// ftInvWeightOpenCurrentContainer / ftInvGpGoUp (PHP + player-character-sheet onclick), and
// ftInvWeightCanGoUp / ftInvWeightCanGoForward / ftInvWeightGoForward (reached as bare globals by
// template.js's updateNavigation — typeof-guarded, so the post-template.js load order is safe). All
// other helpers stay module-local (grep proved zero external callers). Loaded AFTER template.js so
// queryData / getSidelogContext / forceOpenSideInfoPanel / sideInfoActivateItemTab / checkIfValidUUID /
// BASE_URL / openItemInfo / updateNavigation resolve at call time; Chart / ChartDataLabels are vendor
// globals.

const w = window;

interface InventoryBreakdownNode {
	id?: string;
	name?: string;
	weight?: unknown;
	gp?: unknown;
	container?: boolean;
	children?: InventoryBreakdownNode[];
	[key: string]: unknown;
}

interface InventoryBreakdownFrame {
	nodes: InventoryBreakdownNode[];
	subtitle: string | null;
	containerId?: string | null;
}

interface InventoryWeightState {
	stack: InventoryBreakdownFrame[];
	forwardStack: InventoryBreakdownFrame[];
	currentNodes: InventoryBreakdownNode[];
	currentContainerId: string | null;
}

interface InventoryGpState {
	stack: InventoryBreakdownFrame[];
	currentNodes: InventoryBreakdownNode[];
}

function breakdownRecord(value: unknown): Record<string, unknown> | null {
	return value !== null && typeof value === 'object' && !Array.isArray(value)
		? value as Record<string, unknown>
		: null;
}

/**
 * Full helper/cache sidelog context when available; otherwise a minimal stub so container-only
 * breakdown requests still succeed (ability/item panels, sparse markup).
 * @param {string} containerInventoryId
 * @returns {object|null}
 */
function getSidelogContextOrStubForContainerBreakdown(containerInventoryId: unknown) {
	const sl = getSidelogContext();
	if (sl) {
		return sl;
	}
	if (!containerInventoryId || !checkIfValidUUID(containerInventoryId)) {
		return null;
	}
	const session = window.ftGetCampaignSessionAttendance?.() || {};
	const gn = (id: string) => Number((document.getElementById(id) as HTMLInputElement | null)?.value ?? 0) || 0;
	return {
		user_id: Number(window.FateTide?.user?.id ?? 0) || 0,
		roll_private: window.ftGetRollPrivate?.() ?? 0,
		campaign_id: window.ftGetCampaignId?.() || '',
		location_id: getSidelogContext()?.location_id || '',
		encounter_id: window.ftGetActiveEncounterId?.() || '',
		caster_id: window.ftGetActiveCasterId?.() || '',
		caster_main_id: window.ftGetMasterCasterId?.() || '',
		raw_dice_mod: gn('raw_dice_mod'),
		raw_dice_adv_dis_stacks: gn('raw_dice_adv_dis_stacks'),
		caster_active_in_campaign_session: session.caster_active_in_campaign_session || '',
		next_campaign_session_id: session.next_campaign_session_id || '',
		next_campaign_session_start_time: session.next_campaign_session_start_time || '',
		next_campaign_session_end_time: session.next_campaign_session_end_time || '',
	};
}

/** Both charts use the sheet's public renderer; the cache owner performs cold hydration. */
async function openBreakdown(override: unknown, mode: 'whole' | 'on_hand' | 'container', container: unknown = '', metric: 'weight' | 'gp' = 'weight'): Promise<void> {
    forceOpenSideInfoPanel();
    const context = getSidelogContextOrStubForContainerBreakdown(container);
    const casterId = String(override && checkIfValidUUID(override) ? override : context?.caster_id ?? '');
    const containerId = String(container ?? '');
    const host = document.getElementById('div_refresh_side_info');
    if (!casterId && mode !== 'container') { refuseUnported('inventory-breakdown-context'); return; }
    let html = window.ftRenderInventoryWeightBreakdown?.(casterId, mode, containerId, metric);
    if (html == null) {
        const cacheOwner = window as unknown as {
            fetchAndCacheCaster?: (id: string) => Promise<unknown>;
            fetchAndCacheObject?: (id: string) => Promise<unknown>;
            ftResolveInventoryDisplayRow?: (value: unknown) => Promise<Record<string, unknown>>;
        };
        try {
            let resolvedContainer: Record<string, unknown> | undefined;
            if (mode === 'container' && checkIfValidUUID(containerId)) {
                const shell = await cacheOwner.fetchAndCacheObject?.(containerId);
                if (shell) resolvedContainer = await cacheOwner.ftResolveInventoryDisplayRow?.(shell);
            } else if (casterId) await cacheOwner.fetchAndCacheCaster?.(casterId);
            html = window.ftRenderInventoryWeightBreakdown?.(casterId, mode, containerId, metric, resolvedContainer);
        } catch (error) { console.error('Inventory breakdown input unavailable', error); }
    }
    if (!host || html == null) {
        refuseUnported('inventory-breakdown-input-unavailable', { casterId, mode, containerId, metric });
        if (host) host.textContent = 'Inventory data could not be loaded for this breakdown.';
        return;
    }
    host.innerHTML = html;
    sideInfoActivateItemTab();
    if (metric === 'gp') await initializeInventoryGpPieChart();
    else await initializeInventoryWeightPieChart();
}
function openInventoryWeightBreakdown(override: unknown = '') { return openBreakdown(override, 'whole'); }
function openInventoryOnHandWeightBreakdown(override: unknown = '') { return openBreakdown(override, 'on_hand'); }
function openContainerWeightBreakdown(override: unknown = '', container: unknown = '') { return openBreakdown(override, 'container', container); }
function openContainerGpBreakdown(override: unknown = '', container: unknown = '') { return openBreakdown(override, 'container', container, 'gp'); }
function openInventoryOnHandGpBreakdown(override: unknown = '') { return openBreakdown(override, 'on_hand', '', 'gp'); }

// Module-scoped drill state. (In the old template.js home this was `var` not `let` because
// updateNavigation() ran at top-level script eval and called ftInvWeightCanGoUp/Forward, which read
// this — a `let` would have been in its TDZ. Here the bundle loads after template.js and those
// updateNavigation calls are typeof-guarded, so the hazard is gone; kept `var` byte-faithfully.)
let _ftInvWeightState: InventoryWeightState | null = null;

function ftInvWeightFilterNodes(nodes: unknown): InventoryBreakdownNode[] {
	if (!Array.isArray(nodes)) {
		return [];
	}
	return nodes.filter((n): n is InventoryBreakdownNode => !!breakdownRecord(n) && (Number(breakdownRecord(n)?.weight) || 0) > 0);
}

function ftInvWeightPalette(n: number) {
	const c = [
		'#28a745',
		'#17a2b8',
		'#0d6efd',
		'#6f42c1',
		'#ffc107',
		'#fd7e14',
		'#20c997',
		'#dc3545',
		'#6c757d',
		'#e83e8c',
	];
	return c[n % c.length];
}

/** Max rows shown under weight / GP breakdown pies (matches slice labels). */
const FT_INV_BREAKDOWN_TOP_LIST_N = 10;

/**
 * Renders a ranked list under the pie for the current drill-down level.
 * @param {HTMLElement|null} hostEl - #ft-inv-weight-top-list-host or #ft-inv-gp-top-list-host
 * @param {object[]} nodes - filtered slice nodes (same order/source as chart)
 * @param {'weight'|'gp'} valueKey
 * @param {string} unitLabel - e.g. "lb" or "gp"
 * @param {number} totalForPerc - denominator for % (same as chart total)
 * @param {?(node: object) => void} [onActivate] - when set, rows become clickable and call this with the
 *   node (weight breakdown: drill into a group/container, or open/jump a leaf). GP omits it → static list.
 */
function ftInvBreakdownRenderTopList(
	hostEl: HTMLElement | null,
	nodes: InventoryBreakdownNode[],
	valueKey: 'weight' | 'gp',
	unitLabel: string,
	totalForPerc: number,
	onActivate: ((node: InventoryBreakdownNode) => void) | null = null,
) {
	const section = hostEl ? hostEl.closest('[id$="-top-list-section"]') : null;
	if (!hostEl) {
		return;
	}
	hostEl.replaceChildren();
	if (!nodes || nodes.length === 0) {
		if (section) {
			section.classList.add('d-none');
		}
		return;
	}
	if (section) {
		section.classList.remove('d-none');
	}
	const sorted = nodes
		.slice()
		.sort((a, b) => (Number(b[valueKey]) || 0) - (Number(a[valueKey]) || 0))
		.slice(0, FT_INV_BREAKDOWN_TOP_LIST_N);
	const denom = totalForPerc > 0 ? totalForPerc : 1;
	const ul = document.createElement('ul');
	ul.className = 'list-unstyled small mb-0';
	for (const n of sorted) {
		const v = Number(n[valueKey]) || 0;
		const perc = ((v / denom) * 100).toFixed(0);
		const li = document.createElement('li');
		li.className = 'd-flex justify-content-between align-items-baseline gap-2 py-1 border-bottom border-secondary border-opacity-25';
		// Clickable when the caller wants interactivity AND the node leads somewhere (drill-down children,
		// or a linked inventory row). Mirrors the pie-slice click: ftInvWeightActivateNode().
		const actionable = typeof onActivate === 'function'
			&& (n.id || (Array.isArray(n.children) && n.children.length));
		if (actionable) {
			li.style.cursor = 'pointer';
			li.setAttribute('role', 'button');
			li.classList.add('ft-inv-breakdown-row--clickable');
			li.addEventListener('click', () => onActivate(n));
		}
		const nameEl = document.createElement('span');
		nameEl.className = 'text-truncate flex-grow-1';
		nameEl.style.minWidth = '0';
		const nm = n.name || 'Item';
		nameEl.textContent = nm;
		nameEl.title = nm;
		const right = document.createElement('span');
		right.className = 'text-nowrap flex-shrink-0';
		const badge = document.createElement('span');
		badge.className = 'badge bg-secondary bg-gradient border border-dark';
		badge.textContent = `${v} ${unitLabel}`;
		const pct = document.createElement('span');
		pct.className = 'text-muted ms-1';
		pct.textContent = `${perc}%`;
		right.appendChild(badge);
		right.appendChild(pct);
		li.appendChild(nameEl);
		li.appendChild(right);
		ul.appendChild(li);
	}
	hostEl.appendChild(ul);
}

function ftInvWeightDestroyChart(canvas: InventoryBreakdownCanvas | null) {
	if (canvas && canvas._ftInvWeightPieChart) {
		canvas._ftInvWeightPieChart.destroy();
		canvas._ftInvWeightPieChart = null;
	}
}

/** Show/hide the Up + "Open in Inventory" header buttons for the current drill state. */
function ftInvWeightSyncNavButtons() {
	const st = _ftInvWeightState;
	const backBtn = document.getElementById('ft-inv-weight-back');
	if (backBtn) {
		backBtn.classList.toggle('d-none', !st || st.stack.length === 0);
	}
	const openInvBtn = document.getElementById('ft-inv-weight-open-inv');
	if (openInvBtn) {
		openInvBtn.classList.toggle('d-none', !st || !st.currentContainerId);
	}
	// Keep the side-info Back/Forward arrows in step with the pie's drill depth.
	if (typeof updateNavigation === 'function') {
		try { updateNavigation(); } catch (e) { /* nav buttons absent on this page */ }
	}
}

/** True when a breakdown is on screen AND the pie can drill up / re-descend (drives the side-info arrows). */
function ftInvWeightCanGoUp() {
	const st = _ftInvWeightState;
	return !!(st && st.stack && st.stack.length && document.getElementById('ft-inv-weight-root'));
}
function ftInvWeightCanGoForward() {
	const st = _ftInvWeightState;
	return !!(st && st.forwardStack && st.forwardStack.length && document.getElementById('ft-inv-weight-root'));
}

/**
 * Switch to the Inventory tab and scroll to + briefly highlight a container card. The container's DOM id
 * IS its inventory_id (tabs/inventory.ts buildContainers / _inventory_inc.php), so getElementById finds it.
 * Best-effort: silently no-ops if the tab/card isn't on this page (e.g. side-info for another creature).
 * @param {string} containerInventoryId
 */
function ftJumpToInventoryContainer(containerInventoryId: unknown) {
	if (!containerInventoryId) {
		return;
	}
	const tabBtn = document.querySelector('[id^="tab_inventory"]') as HTMLElement | null;
	if (tabBtn) {
		try { tabBtn.click(); } catch (e) { /* non-fatal */ }
	}
	window.setTimeout(() => {
		const el = document.getElementById(String(containerInventoryId));
		if (!el) {
			return;
		}
		try {
			el.scrollIntoView({ behavior: 'smooth', block: 'center' });
		} catch (e) {
			el.scrollIntoView();
		}
		const prevTransition = el.style.transition;
		const prevShadow = el.style.boxShadow;
		el.style.transition = 'box-shadow 0.25s ease-in-out';
		el.style.boxShadow = '0 0 0 3px var(--bs-info, #0dcaf0)';
		window.setTimeout(() => {
			el.style.boxShadow = prevShadow;
			el.style.transition = prevTransition;
		}, 1600);
	}, 80);
}

/** Header "Open in Inventory" button → jump to the container we're currently drilled inside. */
function ftInvWeightOpenCurrentContainer() {
	const st = _ftInvWeightState;
	if (st && st.currentContainerId) {
		ftJumpToInventoryContainer(st.currentContainerId);
	}
}

/**
 * Activate a breakdown node (pie slice or Top-10 row click): drill into a group/non-empty container,
 * else link the leaf straight to its inventory row — container → jump to the Inventory tab; item →
 * open the item info panel.
 */
function ftInvWeightActivateNode(node: InventoryBreakdownNode | undefined) {
	const st = _ftInvWeightState;
	if (!st || !node) {
		return;
	}
	const crumb = document.getElementById('ft-inv-weight-breadcrumb');
	const next = ftInvWeightFilterNodes(node.children);
	if (next.length) {
		st.stack.push({
			nodes: st.currentNodes,
			subtitle: crumb ? crumb.textContent : '',
			containerId: st.currentContainerId || null,
		});
		st.forwardStack = []; // drilling a new branch invalidates any redo history
		st.currentNodes = next;
		st.currentContainerId = (node.container && node.id) ? node.id : null;
		if (crumb) {
			crumb.textContent = `Inside: ${node.name || 'Item'}`;
		}
		ftInvWeightRenderPieChart();
		return;
	}
	if (node.id) {
		if (node.container) {
			ftJumpToInventoryContainer(node.id);
		} else if (typeof openItemInfo === 'function') {
			openItemInfo(node.id);
		}
	}
}

function ftInvWeightRenderPieChart() {
	const canvas = document.getElementById('ftInvWeightPieCanvas') as InventoryBreakdownCanvas | null;
	const crumb = document.getElementById('ft-inv-weight-breadcrumb');
	const st = _ftInvWeightState;
	if (!st || !canvas) {
		return;
	}

	ftInvWeightDestroyChart(canvas);

	const nodes = ftInvWeightFilterNodes(st.currentNodes);
	if (nodes.length === 0) {
		if (crumb) {
			crumb.textContent = st.stack.length ? 'Nothing to show at this level.' : 'No weighted inventory to chart.';
		}
		ftInvWeightSyncNavButtons();
		ftInvBreakdownRenderTopList(document.getElementById('ft-inv-weight-top-list-host'), [], 'weight', 'lb', 1, ftInvWeightActivateNode);
		return;
	}

	const labels = nodes.map((n) => n.name || 'Item');
	const values = nodes.map((n) => Number(n.weight) || 0);
	const colors = nodes.map((_, i) => ftInvWeightPalette(i));
	const total = values.reduce((a, b) => a + b, 0) || 1;
	const ctx = canvas.getContext('2d');

	const chart = new Chart(ctx, {
		type: 'pie',
		data: {
			labels,
			datasets: [
				{
					data: values,
					backgroundColor: colors,
					borderColor: '#212529',
					borderWidth: 2,
				},
			],
		},
		options: {
			responsive: true,
			maintainAspectRatio: false,
			layout: { padding: 12 },
			plugins: {
				legend: { display: false },
				tooltip: {
					callbacks: {
						label(ctx: { raw: number }) {
							const v = ctx.raw;
							const perc = ((v / total) * 100).toFixed(0);
							return `${v} lb (${perc}%)`;
						},
					},
				},
				datalabels: {
					color: '#ffffff',
					font: { size: 10, weight: '700' },
					formatter: (value: number) => {
						const perc = ((value / total) * 100).toFixed(0);
						return `${value}\n${perc}%`;
					},
					anchor: 'center',
					align: 'center',
					shadowBlur: 3,
					shadowColor: 'rgba(0, 0, 0, 0.8)',
					shadowOffsetX: 1,
					shadowOffsetY: 1,
				},
			},
			onClick: (_evt: unknown, elements: Array<{ index: number }>) => {
				if (!elements.length) {
					return;
				}
				const hit = elements[0];
				if (hit) ftInvWeightActivateNode(nodes[hit.index]);
			},
		},
		plugins: typeof ChartDataLabels !== 'undefined' ? [ChartDataLabels] : [],
	});
	canvas._ftInvWeightPieChart = chart;
	ftInvWeightSyncNavButtons();
	ftInvBreakdownRenderTopList(document.getElementById('ft-inv-weight-top-list-host'), nodes, 'weight', 'lb', total, ftInvWeightActivateNode);
}

async function initializeInventoryWeightPieChart() {
	const root = document.getElementById('ft-inv-weight-root');
	const canvas = document.getElementById('ftInvWeightPieCanvas') as InventoryBreakdownCanvas | null;
	const crumb = document.getElementById('ft-inv-weight-breadcrumb');
	if (!root || !canvas) {
		return;
	}

	let tree: Record<string, unknown>;
	try {
		tree = breakdownRecord(JSON.parse(root.dataset.tree || '{}')) ?? {};
	} catch (e) {
		console.error('initializeInventoryWeightPieChart: bad JSON', e);
		return;
	}

	ftInvWeightDestroyChart(canvas);

	const startNodes = ftInvWeightFilterNodes(tree.children);
	_ftInvWeightState = {
		stack: [],
		forwardStack: [],
		currentNodes: startNodes,
		currentContainerId: null,
	};
	if (crumb) {
		crumb.textContent = root.dataset.startBreadcrumb || 'By equipment slot and loose gear';
	}
	ftInvWeightRenderPieChart();
}

function ftInvWeightGoUp() {
	const st = _ftInvWeightState;
	const crumb = document.getElementById('ft-inv-weight-breadcrumb');
	if (!st || !st.stack.length) {
		return;
	}
	// Remember the level we're leaving so the side-info Forward arrow can re-descend into it.
	st.forwardStack = st.forwardStack || [];
	st.forwardStack.push({
		nodes: st.currentNodes,
		subtitle: crumb ? crumb.textContent : '',
		containerId: st.currentContainerId || null,
	});
	const prev = st.stack.pop();
	if (!prev) return;
	st.currentNodes = prev.nodes;
	st.currentContainerId = prev.containerId || null;
	if (crumb) {
		const rootNav = document.getElementById('ft-inv-weight-root');
		crumb.textContent =
			prev.subtitle ||
			(rootNav && rootNav.dataset.startBreadcrumb) ||
			'By equipment slot and loose gear';
	}
	ftInvWeightRenderPieChart();
}

/** Re-descend into the level the user last backed out of (side-info Forward arrow / redo). */
function ftInvWeightGoForward() {
	const st = _ftInvWeightState;
	const crumb = document.getElementById('ft-inv-weight-breadcrumb');
	if (!st || !st.forwardStack || !st.forwardStack.length) {
		return;
	}
	const frame = st.forwardStack.pop();
	if (!frame) return;
	st.stack.push({
		nodes: st.currentNodes,
		subtitle: crumb ? crumb.textContent : '',
		containerId: st.currentContainerId || null,
	});
	st.currentNodes = frame.nodes;
	st.currentContainerId = frame.containerId || null;
	if (crumb) {
		crumb.textContent = frame.subtitle || 'Inside…';
	}
	ftInvWeightRenderPieChart();
}

let _ftInvGpState: InventoryGpState | null = null;

function ftInvGpFilterNodes(nodes: unknown): InventoryBreakdownNode[] {
	if (!Array.isArray(nodes)) {
		return [];
	}
	return nodes.filter((n): n is InventoryBreakdownNode => !!breakdownRecord(n) && (Number(breakdownRecord(n)?.gp) || 0) > 0);
}

function ftInvGpDestroyChart(canvas: InventoryBreakdownCanvas | null) {
	if (canvas && canvas._ftInvGpPieChart) {
		canvas._ftInvGpPieChart.destroy();
		canvas._ftInvGpPieChart = null;
	}
}

function ftInvGpRenderPieChart() {
	const canvas = document.getElementById('ftInvGpPieCanvas') as InventoryBreakdownCanvas | null;
	const crumb = document.getElementById('ft-inv-gp-breadcrumb');
	const backBtn = document.getElementById('ft-inv-gp-back');
	const st = _ftInvGpState;
	if (!st || !canvas) {
		return;
	}

	ftInvGpDestroyChart(canvas);

	const nodes = ftInvGpFilterNodes(st.currentNodes);
	if (nodes.length === 0) {
		if (crumb) {
			crumb.textContent = st.stack.length ? 'Nothing to show at this level.' : 'No GP value to chart.';
		}
		if (backBtn) {
			backBtn.classList.toggle('d-none', st.stack.length === 0);
		}
		ftInvBreakdownRenderTopList(document.getElementById('ft-inv-gp-top-list-host'), [], 'gp', 'gp', 1);
		return;
	}

	const labels = nodes.map((n) => n.name || 'Item');
	const values = nodes.map((n) => Number(n.gp) || 0);
	const colors = nodes.map((_, i) => ftInvWeightPalette(i));
	const total = values.reduce((a, b) => a + b, 0) || 1;
	const ctx = canvas.getContext('2d');

	const chart = new Chart(ctx, {
		type: 'pie',
		data: {
			labels,
			datasets: [
				{
					data: values,
					backgroundColor: colors,
					borderColor: '#212529',
					borderWidth: 2,
				},
			],
		},
		options: {
			responsive: true,
			maintainAspectRatio: false,
			layout: { padding: 12 },
			plugins: {
				legend: { display: false },
				tooltip: {
					callbacks: {
						label(ctx: { raw: number }) {
							const v = ctx.raw;
							const perc = ((v / total) * 100).toFixed(0);
							return `${v} gp (${perc}%)`;
						},
					},
				},
				datalabels: {
					color: '#ffffff',
					font: { size: 10, weight: '700' },
					formatter: (value: number) => {
						const perc = ((value / total) * 100).toFixed(0);
						return `${value}\n${perc}%`;
					},
					anchor: 'center',
					align: 'center',
					shadowBlur: 3,
					shadowColor: 'rgba(0, 0, 0, 0.8)',
					shadowOffsetX: 1,
					shadowOffsetY: 1,
				},
			},
			onClick: (_evt: unknown, elements: Array<{ index: number }>) => {
				if (!elements.length) {
					return;
				}
				const hit = elements[0];
				if (!hit) return;
				const idx = hit.index;
				const picked = nodes[idx];
				if (!picked) return;
				const next = ftInvGpFilterNodes(picked.children);
				if (!next.length) {
					return;
				}
				st.stack.push({
					nodes: st.currentNodes,
					subtitle: crumb ? crumb.textContent : '',
				});
				st.currentNodes = next;
				if (crumb) {
					crumb.textContent = `Inside: ${picked.name || 'Item'}`;
				}
				ftInvGpRenderPieChart();
			},
		},
		plugins: typeof ChartDataLabels !== 'undefined' ? [ChartDataLabels] : [],
	});
	canvas._ftInvGpPieChart = chart;
	if (backBtn) {
		backBtn.classList.toggle('d-none', st.stack.length === 0);
	}
	ftInvBreakdownRenderTopList(document.getElementById('ft-inv-gp-top-list-host'), nodes, 'gp', 'gp', total);
}

async function initializeInventoryGpPieChart() {
	const root = document.getElementById('ft-inv-gp-root');
	const canvas = document.getElementById('ftInvGpPieCanvas') as InventoryBreakdownCanvas | null;
	const crumb = document.getElementById('ft-inv-gp-breadcrumb');
	if (!root || !canvas) {
		return;
	}

	let tree: Record<string, unknown>;
	try {
		tree = breakdownRecord(JSON.parse(root.dataset.tree || '{}')) ?? {};
	} catch (e) {
		console.error('initializeInventoryGpPieChart: bad JSON', e);
		return;
	}

	ftInvGpDestroyChart(canvas);

	const startNodes = ftInvGpFilterNodes(tree.children);
	_ftInvGpState = {
		stack: [],
		currentNodes: startNodes,
	};
	if (crumb) {
		crumb.textContent = root.dataset.startBreadcrumb || 'Contents GP';
	}
	ftInvGpRenderPieChart();
}

function ftInvGpGoUp() {
	const st = _ftInvGpState;
	const crumb = document.getElementById('ft-inv-gp-breadcrumb');
	if (!st || !st.stack.length) {
		return;
	}
	const prev = st.stack.pop();
	if (!prev) return;
	st.currentNodes = prev.nodes;
	if (crumb) {
		const rootNav = document.getElementById('ft-inv-gp-root');
		crumb.textContent =
			prev.subtitle ||
			(rootNav && rootNav.dataset.startBreadcrumb) ||
			'Contents GP';
	}
	ftInvGpRenderPieChart();
}

// --- public surface (bare-identifier globals reached by PHP onclick + cross-bundle callers +
//     template.js updateNavigation). Helpers not listed here stay module-local (grep-verified). ---
Object.assign(w, {
	openInventoryWeightBreakdown,
	openInventoryOnHandWeightBreakdown,
	openContainerWeightBreakdown,
	openContainerGpBreakdown,
	openInventoryOnHandGpBreakdown,
	ftInvWeightGoUp,
	ftInvWeightGoForward,
	ftInvWeightCanGoUp,
	ftInvWeightCanGoForward,
	ftInvWeightOpenCurrentContainer,
	ftInvGpGoUp,
});

export {};
